import { useCallback, useMemo, useState } from 'react';

import { isClientSide } from '@r1-frontend/shared/helpers/ssr';
import { useIsomorphicLayoutEffect } from '@r1-frontend/shared/hooks/useIsomorphicLayoutEffect';

/**
 * Проверяет переданное медиа-выражение, возвращает результат проверки
 * @param {string} mediaQuery медиавыражение вида: '(max-width: 1000px)'
 * @param {boolean} hasInitMatch учитывать совпадение в init состояние
 * @return {boolean}
 */

export const useMatchMedia = (mediaQuery: string, hasInitMatch?: boolean): boolean => {
    const _mql = useMemo<MediaQueryList | null>(() => {
        if (isClientSide() && window.matchMedia) {
            return window.matchMedia(mediaQuery);
        } else {
            return null;
        }
    }, [mediaQuery]);

    const [isMatch, setIsMatch] = useState(hasInitMatch ? _mql?.matches ?? false : false);

    const handleMediaChange = useCallback((e) => {
        setIsMatch(e.matches);
    }, []);

    useIsomorphicLayoutEffect(() => {
        if (!_mql) {
            return;
        }

        setIsMatch(_mql.matches);

        if ('function' === typeof _mql.addEventListener) {
            _mql.addEventListener('change', handleMediaChange);
        }

        return () => {
            if ('function' === typeof _mql.removeEventListener) {
                _mql.removeEventListener('change', handleMediaChange);
            }
        };
    }, [_mql, handleMediaChange]);

    return isMatch;
};

/**
 * Возвращает медиа-выражение для указанной ширины экрана
 * screenWidth - ширина экрана строкой или числом '768px' или 768
 * isMobileFirst: true - если нужен min-width, по умолчанию max-width
 */
const createMediaQuery = (screenWidth: string | number, isMobileFirst?: boolean): string => {
    const width = typeof screenWidth === 'number' ? `${screenWidth}px` : screenWidth;
    return `(${isMobileFirst ? 'min' : 'max'}-width: ${width})`;
};

/**
 * Позволяет передать требуемую ширину экрана вместо медиа-выражения
 * screenWidth - ширина экрана строкой или числом '768px' или 768
 * isMobileFirst: true - если нужен min-width, по умолчанию max-width
 * hasInitMatch: true - учитывать совпадение в init состояние
 */
export const useMatchMediaByWidth = (screenWidth: string | number, isMobileFirst?: boolean, hasInitMatch?: boolean): boolean => {
    const mq = createMediaQuery(screenWidth, isMobileFirst);
    return useMatchMedia(mq, hasInitMatch);
};
